import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Star, BookOpen } from "lucide-react";
import { api_get, api_post, api_put, api_delete, apiError } from "../../lib/api";
import ImageUploader from "../../components/ImageUploader";
import SmartImage from "../../components/SmartImage";

const blank = {
    title: "",
    subtitle: "",
    category: "",
    imageUrl: "",
    imageAlt: "",
    shortDescription: "",
    longDescription: "",
    distanceDuration: "",
    bestSeason: "",
    bestFor: [],
    buttonText: "Book cab",
    buttonUrl: "/cab-booking",
    slug: "",
    seoTitle: "",
    seoDescription: "",
    isActive: true,
    isFeatured: false,
    displayOrder: 100,
};

const slugify = (s) =>
    (s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

const ManageDestinations = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blank);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const r = await api_get("/admin/destinations");
            setItems(r);
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const open = (it = null) => {
        setEditing(it ? it.id : "new");
        setForm(it ? { ...blank, ...it, bestFor: it.bestFor || [] } : blank);
    };
    const close = () => {
        setEditing(null);
        setForm(blank);
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.title) return toast.error("Title is required.");
        try {
            const payload = {
                ...form,
                slug: form.slug ? slugify(form.slug) : slugify(form.title),
                displayOrder: Number(form.displayOrder) || 100,
                bestFor: Array.isArray(form.bestFor)
                    ? form.bestFor
                    : String(form.bestFor || "")
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
            };
            if (editing === "new") {
                await api_post("/admin/destinations", payload);
                toast.success("Destination created.");
            } else {
                await api_put(`/admin/destinations/${editing}`, payload);
                toast.success("Destination updated.");
            }
            close();
            await load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this destination?")) return;
        try {
            await api_delete(`/admin/destinations/${id}`);
            toast.success("Deleted.");
            await load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    return (
        <div data-testid="manage-destinations">
            <header className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-2xl text-ink font-bold">Destinations</h1>
                    <p className="text-ink-light text-sm">Powers /destinations and individual /destinations/:slug pages.</p>
                </div>
                <button
                    onClick={() => open()}
                    className="px-4 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold text-sm flex items-center gap-1.5 shadow-sm"
                    data-testid="add-destination"
                >
                    <Plus className="w-4 h-4" /> New destination
                </button>
            </header>

            <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-cream-100 text-ink-light">
                        <tr>
                            <th className="text-left px-4 py-3 font-semibold">Image</th>
                            <th className="text-left px-4 py-3 font-semibold">Title / Slug</th>
                            <th className="text-left px-4 py-3 font-semibold">Order</th>
                            <th className="text-left px-4 py-3 font-semibold">Status</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="text-center py-10 text-ink-light">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan="5" className="text-center py-10 text-ink-light">No destinations yet.</td></tr>
                        ) : items.map((it) => (
                            <tr key={it.id} className="border-t border-cream-200 hover:bg-cream-50">
                                <td className="px-4 py-3">
                                    <div className="w-14 h-14 rounded-md overflow-hidden bg-cream-100">
                                        <SmartImage src={it.imageUrl} alt={it.title} className="w-full h-full" />
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-ink flex items-center gap-2">
                                        {it.title}
                                        {it.isFeatured && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />}
                                    </div>
                                    <div className="text-xs text-ink-light">/destinations/{it.slug}</div>
                                </td>
                                <td className="px-4 py-3 text-ink-light">{it.displayOrder}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${it.isActive ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                        {it.isActive ? "Active" : "Inactive"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-1">
                                        <Link
                                            to={`/admin/destinations/${it.slug}/edit`}
                                            className="px-2.5 py-1.5 rounded bg-ink text-white text-xs font-semibold flex items-center gap-1 hover:bg-brand"
                                            data-testid={`edit-guide-${it.slug}`}
                                            title="Edit full travel guide"
                                        >
                                            <BookOpen className="w-3.5 h-3.5" /> Guide
                                        </Link>
                                        <button onClick={() => open(it)} className="p-1.5 rounded hover:bg-cream-100" data-testid={`edit-dest-${it.slug}`} title="Quick edit basics">
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => remove(it.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`delete-dest-${it.slug}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
                    <form
                        onSubmit={save}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-2xl w-full max-w-3xl my-8 p-6 shadow-xl"
                    >
                        <header className="flex items-center justify-between mb-5">
                            <h2 className="font-display text-xl font-bold">
                                {editing === "new" ? "New destination" : "Edit destination"}
                            </h2>
                            <button type="button" onClick={close} className="p-1 rounded hover:bg-cream-100">
                                <X className="w-5 h-5" />
                            </button>
                        </header>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Title *" value={form.title} onChange={(v) => setForm({ ...form, title: v, slug: form.slug || slugify(v) })} testId="dest-title" />
                            <Field label="Slug *" value={form.slug} onChange={(v) => setForm({ ...form, slug: slugify(v) })} testId="dest-slug" />
                            <Field label="Subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
                            <Field label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
                            <Field label="Distance / duration" value={form.distanceDuration} onChange={(v) => setForm({ ...form, distanceDuration: v })} />
                            <Field label="Best season" value={form.bestSeason} onChange={(v) => setForm({ ...form, bestSeason: v })} />
                            <div className="md:col-span-2">
                                <Field label="Best for (comma-separated)" value={(form.bestFor || []).join(", ")} onChange={(v) => setForm({ ...form, bestFor: v.split(",").map((s) => s.trim()).filter(Boolean) })} />
                            </div>
                            <div className="md:col-span-2">
                                <ImageUploader value={form.imageUrl} onChange={(v) => setForm({ ...form, imageUrl: v })} label="Image" testId="dest-image" />
                            </div>
                            <div className="md:col-span-2">
                                <Field label="Image alt (SEO)" value={form.imageAlt} onChange={(v) => setForm({ ...form, imageAlt: v })} />
                            </div>
                            <div className="md:col-span-2">
                                <Textarea label="Short description" value={form.shortDescription} onChange={(v) => setForm({ ...form, shortDescription: v })} rows={2} />
                            </div>
                            <div className="md:col-span-2">
                                <Textarea label="Long description" value={form.longDescription} onChange={(v) => setForm({ ...form, longDescription: v })} rows={5} />
                            </div>
                            <Field label="Button text" value={form.buttonText} onChange={(v) => setForm({ ...form, buttonText: v })} />
                            <Field label="Button URL" value={form.buttonUrl} onChange={(v) => setForm({ ...form, buttonUrl: v })} />
                            <Field label="Display order" type="number" value={form.displayOrder} onChange={(v) => setForm({ ...form, displayOrder: Number(v) })} />
                            <div className="flex items-center gap-4 mt-7">
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} data-testid="dest-active" />
                                    Active
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.isFeatured} onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })} data-testid="dest-featured" />
                                    Featured (hero card)
                                </label>
                            </div>
                            <div className="md:col-span-2 pt-3 border-t border-cream-200">
                                <h3 className="font-display text-sm text-ink-light mb-2">SEO</h3>
                            </div>
                            <Field label="Meta title" value={form.seoTitle} onChange={(v) => setForm({ ...form, seoTitle: v })} />
                            <Field label="Meta description" value={form.seoDescription} onChange={(v) => setForm({ ...form, seoDescription: v })} />
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-cream-200">
                                Cancel
                            </button>
                            <button type="submit" className="px-5 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold" data-testid="dest-save">
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, value, onChange, type = "text", testId }) => (
    <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">{label}</label>
        <input
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white text-sm"
            data-testid={testId}
        />
    </div>
);
const Textarea = ({ label, value, onChange, rows = 3 }) => (
    <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">{label}</label>
        <textarea
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white text-sm"
        />
    </div>
);

export default ManageDestinations;
